import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ModalIconButtonTrigger } from '@atlassian/entry-points/modal-icon-button-trigger';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';

import type { GicModalProps } from '../../../../../../common/ui/gic-modal';
import { gicModalEntryPoint } from '../../../../../../common/ui/gic-modal/entrypoint/gicModal.entrypoint';
import { isSubTaskWithoutCreatedParent } from '../../../../../../common/utils/jira-create-with-rovo-preview-card';

import type { rowActions_AtlassianConversationAssistantPreviewCard$key as RowActionsProjectKey } from './__generated__/rowActions_AtlassianConversationAssistantPreviewCard.graphql';

const messages = defineMessages({
	editWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.editWorkItem',
		defaultMessage: 'Edit work item',
		description: 'Label for the button to edit a work item suggestion',
	},
	declineWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.declineWorkItem',
		defaultMessage: 'Decline work item',
		description: 'Label for the button to decline a work item suggestion',
	},
	acceptWorkItem: {
		id: 'jiraCreateWithRovoPreviewCard.acceptWorkItem',
		defaultMessage: 'Accept work item',
		description: 'Label for the button to accept a work item suggestion',
	},
	disabledInlineCreate: {
		id: 'jiraCreateWithRovoPreviewCard.disabledInlineCreate',
		defaultMessage: 'Create or add a parent to accept this work item.',
		description: 'Tooltip text for the button to accept a work item suggestion',
	},
	disabledInlineEdit: {
		id: 'jiraCreateWithRovoPreviewCard.disabledInlineEdit',
		defaultMessage: 'Create or add a parent to edit this work item.',
		description: 'Tooltip text for the button to edit a work item suggestion',
	},
});

const styles = cssMap({
	inlineButtons: {
		position: 'absolute',
		right: token('space.100'),
		cursor: 'pointer',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

type RowActionsProps = {
	projectRef: RowActionsProjectKey;
	draftWorkItem: DraftWorkItem;
	onDeclineWorkItem: (args: { invocationId: string }) => void;
	onInlineAccept: () => void;
	isCreating: boolean;
	showRowActions: boolean;
	gicEntryPointProps: GicModalProps;
	isStagingArea: boolean;
	itemCount: number;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
};

export const RowActions = ({
	projectRef,
	draftWorkItem,
	onDeclineWorkItem,
	onInlineAccept,
	isCreating,
	showRowActions,
	gicEntryPointProps,
	isStagingArea,
	itemCount,
	sendAifcAnalyticsEvent,
}: RowActionsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const projectData = useFragment<RowActionsProjectKey>(
		graphql`
			fragment rowActions_AtlassianConversationAssistantPreviewCard on JiraProject {
				projectId
			}
		`,
		projectRef,
	);

	const projectId = projectData?.projectId ?? undefined;

	const handleOnDecline = useCallback(() => {
		onDeclineWorkItem({ invocationId: draftWorkItem.invocationId });
	}, [onDeclineWorkItem, draftWorkItem.invocationId]);

	const isSubTaskWithoutParent = isSubTaskWithoutCreatedParent(draftWorkItem);

	const showActions = isCreating || showRowActions;

	// Don't show actions if the work item has already been created
	const hasCreatedIssueKey = draftWorkItem.createdIssueKey;

	const onGicClick = useCallback(() => {
		sendAifcAnalyticsEvent?.({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createWorkItemsEditInGic',
			attributes: {
				itemCount,
				isSingleWorkItem: false,
				isStagingArea,
			},
		});
	}, [sendAifcAnalyticsEvent, isStagingArea, itemCount]);

	if (!showActions || hasCreatedIssueKey) {
		return null;
	}

	return (
		<Inline alignBlock="center" xcss={styles.inlineButtons}>
			{!FeatureGates.getExperimentValue(
				'jira-aifc-workflow-resumption-ui-updates',
				'isEnabled',
				false,
			) && (
				<Tooltip
					content={
						isSubTaskWithoutParent ? formatMessage(messages.disabledInlineCreate) : undefined
					}
				>
					<IconButton
						icon={(iconProps) => <CheckMarkIcon {...iconProps} size="small" />}
						label={formatMessage(messages.acceptWorkItem)}
						appearance="subtle"
						spacing="compact"
						onClick={onInlineAccept}
						isDisabled={isSubTaskWithoutParent}
						isLoading={isCreating}
					/>
				</Tooltip>
			)}
			<Tooltip
				content={isSubTaskWithoutParent ? formatMessage(messages.disabledInlineEdit) : undefined}
			>
				<ModalIconButtonTrigger
					icon={(iconProps) => <EditIcon {...iconProps} size="small" />}
					label={formatMessage(messages.editWorkItem)}
					appearance="subtle"
					spacing="compact"
					entryPoint={gicModalEntryPoint}
					onClick={onGicClick}
					// We don't support inline editing of subtasks, and disable all actions while creating
					isDisabled={isSubTaskWithoutParent || isCreating}
					entryPointProps={{
						...gicEntryPointProps,
						selectedDraftWorkItem: draftWorkItem,
						projectId,
					}}
					entryPointParams={{
						cloudId: gicEntryPointProps.cloudId,
						parentIssue: (draftWorkItem?.fields?.parent as string | undefined) ?? undefined,
					}}
					modalProps={{
						width: 'large',
						shouldScrollInViewport: true,
					}}
				/>
			</Tooltip>
			<IconButton
				icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
				label={formatMessage(messages.declineWorkItem)}
				appearance="subtle"
				spacing="compact"
				onClick={handleOnDecline}
				isDisabled={isCreating}
			/>
		</Inline>
	);
};
